import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import {
  downloadVaultDirectoryZip,
  GaiaEventType,
  startVaultDirectoryDownload,
  TaskStatus,
  WebsocketSubscriptionTarget,
  type GaiaEvent,
  type VaultZipDownloadPayload,
} from '@/api';
import { WEBSOCKET_DISABLED_KEY } from '@/composables/useReviewWebSocket';
import { getWsClient } from '@/services/WebSocketService';

// A panel row IS the (self-describing) websocket payload — no supplementary
// local type. The row's `status` is frontend-managed, though: it stays
// `running` after the `done` *event* until the artifact fetch actually
// saves the file, and flips to `failed` if that fetch fails.

function asZipPayload(data: GaiaEvent['data']): VaultZipDownloadPayload | null {
  return typeof data === 'object' && 'status' in data && 'processed_files' in data ? data : null;
}

function triggerBrowserDownload(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();

  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 100);
}

/**
 * Vault folder-zip downloads run as async backend jobs: POST kickoff →
 * throttled `vault_zip_download_updated` progress events on the websocket →
 * GET the finished artifact. This store owns the job list shown in the global
 * bottom-right DownloadsPanel, so jobs (and their websocket subscriptions)
 * survive route changes — which is why the subscription lifecycle lives here
 * rather than in a component-scoped composable.
 */
export const useDownloadManagerStore = defineStore('downloadManager', () => {
  const jobs = ref<VaultZipDownloadPayload[]>([]);

  const hasJobs = computed(() => jobs.value.length > 0);
  const activeJobCount = computed(
    () => jobs.value.filter(j => j.status === TaskStatus.RUNNING).length
  );

  // Non-reactive bookkeeping: one refcounted uid subscription per job, plus a
  // single shared event handler released once the last job's uid is released
  // (letting the service's idle-close reclaim the socket).
  const uidDisposers = new Map<string, () => void>();
  let handlerDisposer: (() => void) | null = null;

  function acquireSubscription(uid: string): void {
    const wsClient = getWsClient();
    handlerDisposer ??= wsClient.registerHandler(
      WebsocketSubscriptionTarget.VAULT_ZIP_DOWNLOAD,
      onZipEvent
    );
    uidDisposers.set(
      uid,
      wsClient.addSubscriptionUid(WebsocketSubscriptionTarget.VAULT_ZIP_DOWNLOAD, uid)
    );
  }

  function releaseSubscription(uid: string): void {
    const dispose = uidDisposers.get(uid);
    if (dispose) {
      dispose();
      uidDisposers.delete(uid);
    }
    if (uidDisposers.size === 0 && handlerDisposer) {
      handlerDisposer();
      handlerDisposer = null;
    }
  }

  function findJob(uid: string): VaultZipDownloadPayload | undefined {
    return jobs.value.find(job => job.uid === uid);
  }

  // Completed rows clear themselves shortly after the zip is saved (Drive-style);
  // error rows stay until the user dismisses them.
  const AUTO_DISMISS_DELAY_MS = 3000;

  function scheduleAutoDismiss(uid: string): void {
    setTimeout(() => {
      if (findJob(uid)?.status === TaskStatus.DONE) {
        dismissJob(uid);
      }
    }, AUTO_DISMISS_DELAY_MS);
  }

  function onZipEvent(event: GaiaEvent): void {
    if (event.gaia_event_type !== GaiaEventType.VAULT_ZIP_DOWNLOAD_UPDATED) {
      return;
    }
    const payload = asZipPayload(event.data);
    if (!payload) {
      return;
    }
    // The shared handler receives every zip event for the user — filter by uid.
    const job = findJob(payload.uid);
    if (!job || job.status !== TaskStatus.RUNNING) {
      return;
    }

    job.processed_files = payload.processed_files;
    job.total_files = payload.total_files;

    job.file_name = payload.file_name;

    if (payload.status === TaskStatus.DONE) {
      job.artifact_uid = payload.artifact_uid ?? null;
      releaseSubscription(job.uid);
      void fetchAndSave(job);
    } else if (payload.status === TaskStatus.FAILED) {
      job.status = TaskStatus.FAILED;
      job.error = payload.error ?? 'Failed to build the zip.';
      releaseSubscription(job.uid);
    }
  }

  async function fetchAndSave(job: VaultZipDownloadPayload): Promise<void> {
    try {
      if (!job.artifact_uid) {
        throw new Error('The finished zip is missing its download id.');
      }
      const response = await downloadVaultDirectoryZip({
        path: { artifact_uid: job.artifact_uid },
      });
      // The generated response type is `unknown`, but the client parses the
      // `application/zip` body to a Blob — narrow instead of casting.
      if (!(response.data instanceof Blob)) {
        throw new TypeError('Unexpected zip response');
      }
      triggerBrowserDownload(response.data, job.file_name);
      job.status = TaskStatus.DONE;
      job.error = null;
      scheduleAutoDismiss(job.uid);
    } catch (error) {
      // Keep artifact_uid so "Download again" can still retry the fetch.
      job.status = TaskStatus.FAILED;
      job.error = error instanceof Error ? error.message : 'Failed to download the zip.';
    }
  }

  async function startDirectoryDownload(directoryUid: string): Promise<void> {
    if (localStorage.getItem(WEBSOCKET_DISABLED_KEY) === 'true') {
      throw new Error('Folder downloads need live updates, which are disabled in settings.');
    }
    // Already zipping this directory — the existing panel row is the feedback.
    if (
      jobs.value.some(
        job => job.directory_uid === directoryUid && job.status === TaskStatus.RUNNING
      )
    ) {
      return;
    }

    // Warm the socket before the kickoff so the subscribe frame below sends
    // immediately — the worker starts emitting events as soon as the POST returns.
    await getWsClient().openConnection();

    const response = await startVaultDirectoryDownload({ path: { uid: directoryUid } });
    if (!response.data) {
      throw new Error('Failed to start folder download');
    }

    jobs.value.push({
      uid: response.data.download_uid,
      directory_uid: directoryUid,
      file_name: response.data.file_name,
      total_files: response.data.total_files,
      processed_files: 0,
      status: TaskStatus.RUNNING,
      artifact_uid: null,
      error: null,
    });
    acquireSubscription(response.data.download_uid);
  }

  /** Re-fetch the finished artifact of a completed (or fetch-failed) job. */
  async function redownloadJob(uid: string): Promise<void> {
    const job = findJob(uid);
    if (!job?.artifact_uid) {
      return;
    }
    job.error = null;
    await fetchAndSave(job);
  }

  /** Restart a failed job that never produced an artifact. */
  async function retryJob(uid: string): Promise<void> {
    const job = findJob(uid);
    if (!job || job.status !== TaskStatus.FAILED) {
      return;
    }
    const directoryUid = job.directory_uid;
    dismissJob(uid);
    await startDirectoryDownload(directoryUid);
  }

  function dismissJob(uid: string): void {
    // Dismissing an in-flight job just abandons it: there is no backend cancel,
    // and the periodic sweep reaps the orphaned artifact.
    releaseSubscription(uid);
    jobs.value = jobs.value.filter(job => job.uid !== uid);
  }

  return {
    jobs,
    hasJobs,
    activeJobCount,
    startDirectoryDownload,
    redownloadJob,
    retryJob,
    dismissJob,
  };
});
